import { HttpClient } from '@adapters/HttpClient';
import { Preparation, Product, ProductsApiResponse, PackingProduct, PackingData } from '../models/Packing';
import { Order, OrdersApiResponse } from '../models/Orders';
import { Location } from '@modules/OrderPreparation/shared';

export class PackingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Obtener una preparación por ID
  async getPreparation(id: number): Promise<Preparation> {
    try {
      const response = await this.httpClient.get<Preparation>(`/preparations/${id}`);
      return response;
    } catch {
      throw new Error(`No se pudo cargar la preparación con ID ${id}`);
    }
  }

  // 🎯 Obtener productos por IDs
  async getProductsByIds(productIds: number[]): Promise<Product[]> {
    try {
      if (productIds.length === 0) {
        return [];
      }

      const productIdsString = productIds.join(',');
      const response = await this.httpClient.get<ProductsApiResponse>(`/products`, {
        fields: 'basic',
        product_ids: productIdsString,
      });

      return response.data || [];
    } catch {
      throw new Error('No se pudieron cargar los productos');
    }
  }

  // 🎯 Obtener packs por IDs
  async getPacksByIds(packIds: number[]): Promise<any[]> {
    try {
      if (packIds.length === 0) {
        return [];
      }

      const packIdsString = packIds.join(',');
      const response = await this.httpClient.get<any>(`/packs`, {
        pack_ids: packIdsString,
      });

      return response.data || response || [];
    } catch {
      throw new Error('No se pudieron cargar los packs');
    }
  }

  // 🎯 Obtener datos completos de Packing
  async getPackingData(id: number): Promise<PackingData> {
    try {
      // 1. Obtener la preparación
      const preparation = await this.getPreparation(id);

      // 2. Validar que sea tipo packing (tipo 3)
      if (preparation.type !== 3) {
        throw new Error('404 - Preparación no encontrada');
      }

      // 3. Validar que tenga líneas
      if (!preparation.lines || preparation.lines.length === 0) {
        throw new Error('La preparación no tiene productos asignados');
      }

      // 4. Extraer los IDs de productos de las líneas
      const productIds = preparation.lines.map((line) => line.product_id);

      // 3. Obtener los productos
      const products = await this.getProductsByIds(productIds);

      // 4. Crear un mapa de productos por ID para acceso rápido
      const productsMap = new Map(products.map((product) => [product.id, product]));

      // 5. Combinar los datos de preparación con los productos
      const packingProducts: PackingProduct[] = preparation.lines
        .map((line) => {
          const product = productsMap.get(line.product_id);

          // Solo incluir productos que existen en la API
          if (!product) {
            return null;
          }

          return {
            id: line.product_id.toString(),
            name: product.name,
            code: product.code,
            image: product.image_hash || '',
            expectedQuantity: line.qty,
            processedQuantity: line.qty_done,
            isCompleted: line.qty_done >= line.qty,
          };
        })
        .filter((product): product is PackingProduct => product !== null);

      const result: PackingData = {
        preparation,
        products: packingProducts,
      };

      return result;
    } catch (error: any) {
      // Si ya es nuestro error específico, propagarlo tal como está
      if (error.message === '404 - Preparación no encontrada') {
        throw error;
      }
      // Para otros errores, envolver en mensaje genérico
      throw new Error(`No se pudieron cargar los datos de Packing para ID ${id}`);
    }
  }

  // 🎯 Obtener pedidos por ID de preparación
  async getOrdersByPreparationId(preparationId: number): Promise<Order[]> {
    try {
      const response = await this.httpClient.get<OrdersApiResponse>('/orders', {
        preparation_ids: preparationId.toString(),
        'sorting[column]': 'id',
        'sorting[direction]': 'asc',
      });

      return response.data || [];
    } catch {
      throw new Error(`No se pudieron cargar los pedidos de la preparación ${preparationId}`);
    }
  }

  // 🎯 Enviar datos de packing por pedido individual
  async sendOrderPackingData(
    preparationId: number,
    orderId: number,
    products: Array<{ product_id: number; quantity: number }>,
    printerCode: string,
  ): Promise<void> {
    try {
      const payload = {
        preparation_id: preparationId,
        order: orderId,
        printer_code: printerCode,
        products: products,
      };

      {
        /* Aqui se genera el Packing */
      }
      await this.httpClient.put(`/preparations/picking/${preparationId}/pack`, payload);
    } catch (error: any) {
      // Preservar el error original de la API
      const apiError =
        error.response?.data?.message || error.message || 'No se pudieron enviar los datos de packing del pedido';
      throw new Error(apiError);
    }
  }

  // 🎯 Enviar datos completos de packing a la API (DEPRECATED - mantener por compatibilidad)
  async sendPackingData(preparationId: number, orders: any[]): Promise<void> {
    try {
      const payload = {
        preparation_id: preparationId,
        orders: orders,
      };

      await this.httpClient.put(`/preparations/${preparationId}/pick-pack-orders`, payload);
    } catch {
      // Error enviando datos de packing
      throw new Error('No se pudieron enviar los datos de packing');
    }
  }

  // 🎯 Obtener ubicaciones por ID de producto
  async getLocationsByProductId(productId: number): Promise<Location[]> {
    try {
      const response = await this.httpClient.get<{
        data: Array<{
          id: number;
          code: string;
          stock: number;
          product: {
            id: number;
            name: string;
            image: string;
            code: string;
          };
        }>;
        meta: {
          total: number;
          perPage: number;
          currentPage: number;
          lastPage: number;
        };
        links: {
          first: string;
          prev: string | null;
          next: string | null;
          last: string;
        };
      }>('/locations', { product_ids: productId.toString() });

      // Devolver las ubicaciones con el id original para el payload
      const locations: Location[] = response.data.map((location) => ({
        id: location.id,
        code: location.code,
        quantity: location.stock,
      }));

      return locations;
    } catch {
      throw new Error(`No se pudieron cargar las ubicaciones del producto ${productId}`);
    }
  }

  // 🎯 Imprimir etiqueta de pedido
  async printOrder(orderId: number, printingType: string = 'pdf', printerCode?: string): Promise<{ link: string }> {
    const params: any = {
      printing_type: printingType,
    };

    if (printerCode) {
      params.printer_code = printerCode;
    }

    return await this.httpClient.get(`/orders/${orderId}/print`, params);
  }
}
